/**
 * pendingEdits — debounced content writes from settings forms.
 *
 * A settings text field writes while the person types, so the canvas follows
 * the text, but not on every keystroke: a draft per item is merged and
 * written once its timer fires. Anything that must see the document current —
 * undo/redo, closing the surface, switching the selection — flushes first, so
 * no timer can write after an undo and nothing typed is lost.
 *
 * Each flush is an undo boundary (`setUndoBoundary`, registered by
 * useEditorDoc as `undoManager.stopCapturing`): a drop and the first settings
 * edit, or two fields, never merge into one undo step.
 */
import { useGridStore } from "@fredericrous/grid";

type Patch = Record<string, unknown>;

interface Draft {
  readonly gridId: string;
  patch: Patch;
  timer: ReturnType<typeof setTimeout>;
}

const drafts = new Map<string, Draft>();
let boundary: (() => void) | null = null;

/** Merge `patch` into the item's contentProps now; a key patched to
 *  `undefined` is removed. A missing item is a no-op (it was deleted while
 *  the edit was pending). */
export function patchContentProps(
  gridId: string,
  itemId: string,
  patch: Patch,
): void {
  const st = useGridStore.getState();
  const cur = st.grids[gridId]?.items[itemId];
  if (!cur) return;
  const next: Patch = { ...(cur.contentProps ?? {}) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete next[k];
    else next[k] = v;
  }
  st.updateItem(gridId, itemId, { contentProps: next });
}

function write(itemId: string): void {
  const d = drafts.get(itemId);
  if (!d) return;
  clearTimeout(d.timer);
  drafts.delete(itemId);
  patchContentProps(d.gridId, itemId, d.patch);
}

/** Queue `patch` for `itemId`; it is written `delayMs` after the last call. */
export function scheduleEdit(
  gridId: string,
  itemId: string,
  patch: Patch,
  delayMs = 150,
): void {
  const prev = drafts.get(itemId);
  if (prev) clearTimeout(prev.timer);
  const timer = setTimeout(() => {
    write(itemId);
    boundary?.();
  }, delayMs);
  drafts.set(itemId, {
    gridId,
    patch: { ...(prev?.patch ?? {}), ...patch },
    timer,
  });
}

/** Write every pending draft now, then mark an undo boundary. */
export function flushPendingEdits(): void {
  if (drafts.size === 0) return;
  for (const itemId of [...drafts.keys()]) write(itemId);
  boundary?.();
}

/** Drop pending drafts — for one item, or all of them. */
export function cancelPendingEdits(itemId?: string): void {
  const ids = itemId === undefined ? [...drafts.keys()] : [itemId];
  for (const id of ids) {
    const d = drafts.get(id);
    if (!d) continue;
    clearTimeout(d.timer);
    drafts.delete(id);
  }
}

/** Whether `itemId` has an unwritten draft. */
export function hasPendingEdit(itemId: string): boolean {
  return drafts.has(itemId);
}

/** Register what marks an undo boundary after each write; returns the
 *  unregister function. */
export function setUndoBoundary(fn: () => void): () => void {
  boundary = fn;
  return () => {
    if (boundary === fn) boundary = null;
  };
}
